import { formatDate, formatBool } from "@/utils";
import permissions from "@/security/permissions";

const ORDER_FILTER_FIELDS = [
  { name: "OrderNumber", displayName: "System #", type: "int" },
  { name: "ShipsWith", displayName: "Ships with System #", type: "int" },
  { name: "CRN", displayName: "CRN", type: "input" },
  { name: "BillTos", displayName: "Bill To", type: "agent-bill-to-select", multiple: true, deliveryForId: -1 },
  {
    name: "ExcludeBillTos",
    displayName: "Exclude Bill To",
    type: "agent-bill-to-select",
    multiple: true, 
    deliveryForId: -1
  },
  {
    name: "ExcludeFacilityCodes",
    displayName: "Exclude Facility",
    type: "facility-select",
    multiple: true
  },
  {
    name: "DeliveryForIds",
    displayName: "Delivery For",
    type: "delivery-for-select",
    multiple: true
  },
  {
    name: "DeliveryTypes",
    displayName: "Delivery Type",
    type: "delivery-type-select",
    multiple: true
  },
  { name: "FacilityCodes", displayName: "Facility", type: "facility-select", multiple: true },
  {
    name: "Manufacturers",
    displayName: "Manufacturer",
    type: "manufacturer-select",
    multiple: true
  },
  { name: "ConsigneeName", displayName: "Consignee Name", type: "input" },
  { name: "ConsigneeAddress", displayName: "Address", type: "input" },
  { name: "ConsigneePhone", displayName: "Consignee Contact Phone Number", type: "input" },
  { name: "ConsigneeCities", displayName: "City", type: "multi-input-field", multiple: true },
  { name: "ConsigneeStates", displayName: "State", type: "multi-input-field", multiple: true },
  { name: "ZipZip3s", displayName: "Zip/Zip3", type: "multi-input-field", multiple: true },
  {
    name: "ExcludeZipZip3s",
    displayName: "Exclude Zip/Zip3",
    type: "multi-input-field",
    multiple: true
  },
  { name: "ShipDate", displayName: "ShipDate", type: "range" },
  { name: "AnticipatedReceiveDate", displayName: "Estimated Received Date", type: "range" },
  { name: "ActualReceiveDate", displayName: "Actual Received Date", type: "range" },
  { name: "RequestedDeliveryDate", displayName: "Requested Delivery Date", type: "range" },
  { name: "ScheduledDeliveryDate", displayName: "Scheduled Delivery Date", type: "range" },
  { name: "ActualDeliveryDate", displayName: "Actual Delivery Date", type: "range" },
  { name: "IntermediateConsignee", displayName: "Intermediate Consignee", type: "input" },
  { name: "TruckName", displayName: "Truck Name", type: "input" },
  {
    name: "TruckStatuses",
    displayName: "Truck Status",
    type: "truck-status-select",
    multiple: true
  },
  { name: "BookedBy", displayName: "Booked By", type: "input" },
  { name: "NotesFlag", displayName: "Notes Flag", type: "bool" },
  { name: "IsScheduled", displayName: "Is Scheduled", type: "bool" },
  {
    name: "OrderStatuses",
    displayName: "Order Status",
    type: "order-status-select",
    multiple: true
  },
  {
    name: "OrderType",
    displayName: "Order Type",
    type: "order-type-select"
  },
  {
    name: "OrderByCompanyIds",
    displayName: "Order By Companies",
    type: "order-by-select",
    deliveryForId: -1,
    multiple: true
  },
  { name: "ConsigneeCompanyId", displayName: "Company Consignee Id", type: "input" },
  { name: "ShipperCompanyId", displayName: "Shipper Company Id", type: "input" },
  { name: "ShipperName", displayName: "Shipper Name", type: "input" },
  { name: "ShipperPhoneNumber", displayName: "Shipper Phone Number", type: "input" },
  { name: "ShipperAddress", displayName: "Shipper Address", type: "input" },
  {
    name: "ShipperCities",
    displayName: "Shipper Cities",
    type: "multi-input-field",
    multiple: true
  },
  {
    name: "ShipperZipCodes",
    displayName: "Shipper Zipcodes",
    type: "multi-input-field",
    multiple: true
  },
  { name: "StartDate", displayName: "Date Placed", type: "range", requiredForAdvancedSearch: true },
  { name: "AlertTypes", displayName: "Alert Type", type: "alert-type-select", multiple: true },
  {
    name: "RefNumbers",
    displayName: "Reference Numbers",
    type: "reference-type-editor",
    advancedOnly: false,
    multiple: true
  },
  {
    name: "Remarks",
    displayName: "Remarks",
    type: "input",
    advancedOnly: false
  },
  { name: "Carrier", displayName: "Carrier", type: "carrier-select", multiple: true },
  { name: "IsReceived", displayName: "Is Received", type: "bool" },
  { name: "IsAsapDelivery", displayName: "Wants ASAP", type: "bool" },
  { name: "IsOnTruck", displayName: "Is On Truck", type: "bool" },
  { name: "HasPOD", displayName: "Has POD", type: "bool" },
  { name: "customerNotReady", displayName: "Customer Not Ready", type: "bool" }
];

const HOME_SHORTCUT = [
  {
    title: "New Order",
    icon: "plus-circle",
    action: "order/newOrderShortcutAction",
    requiresPermission: permissions.order.create,
    requiresAgentPermission: permissions.agents.order.create,
    requiresSelection: false
  }
];

const DISPATCH_SHORTCUT = [
  {
    title: "New Order",
    icon: "plus-circle",
    action: "order/newOrderShortcutAction",
    requiresPermission: permissions.order.create,
    requiresAgentPermission: permissions.agents.order.create,
    requiresSelection: false
  },
  {
    title: "Refresh",
    icon: "sync-alt",
    action: "order/refreshDispatchShortcutAction",
    requiresSelection: false
  },
  {
    title: "Copy",
    icon: "copy",
    action: "order/copyOrderShortcutAction",
    requiresSelection: true,
    requiresPermission: permissions.order.copy,
    requiresAgentPermission: permissions.agents.order.copy
  },
  {
    title: "Add to Truck",
    icon: "truck",
    action: "order/addOrderToTruckShortcutAction",
    requiresPermission: permissions.truck.addOrder,
    requiresSelection: true
  },
  {
    title: "Message Customer",
    icon: "envelope",
    action: "order/messageCustomerShortcutAction",
    requiresSelection: true,
    maxSelection: 1,
    requiresPermission: permissions.order.messageCustomer,
    requiresAgentPermission: permissions.agents.order.messageCustomer
  }
];
const ORDER_MENU_ITEM = [
  { title: "Open Order", action: "order/openOrderShortcutAction", maxSelection: 1 },
  {
    title: "Open Order in New Tab",
    action: "order/openOrderInNewTabShortcutAction",
    maxSelection: 1
  },
  {
    title: "Estimated Receive Date",
    action: "order/estimatedReceiveDateShortcutAction"
  },
  { title: "Preview Order", action: "order/orderPreviewShortcutAction", maxSelection: 1 },
  {
    title: "Receive",
    action: "order/receiveOrderShortcutAction",
    requiresPermission: permissions.order.receive,
    requiresAgentPermission: permissions.agents.order.receive,
  },
  {
    title: "Schedule",
    action: "order/scheduleOrderShortcutAction",
    requiresPermission: permissions.order.schedule,
    requiresAgentPermission: permissions.agents.order.schedule,
  },
  {
    title: "Scheduled Delivery Date History",
    action: "order/deliveryDateHistoryLogsShortcutAction",
    requiresPermission: permissions.order.schedule,
    requiresAgentPermission: permissions.agents.order.schedule
  },
  {
    title: "Unschedule",
    action: "order/unscheduleOrderShortcutAction",
    requiresPermission: permissions.order.unschedule,
    requiresAgentPermission: permissions.agents.order.unschedule,
  },
  {
    title: "Set Wants ASAP",
    action: "order/setBulkOrdersWantsAsapAction",
    requiresPermission: permissions.order.save
  },
  {
    title: "Set Customer Not Ready",
    action: "order/customerNotReadyShortcutAction",
    requiresPermission: permissions.order.customerNotReady,
    requiresAgentPermission: permissions.agents.order.customerNotReady
  },
  {
    title: "Update Order Instructions",
    action: "order/updateOrderInstructionsShortcutAction",
    requriesPermission: permissions.order.edit.specialRequest
  },
  {
    title: "Add to Truck",
    action: "order/addOrderToTruckShortcutAction",
    requiresPermission: permissions.truck.addOrder
  },
  {
    title: "Remove from Truck",
    action: "order/removeOrderFromTruckShortcutAction",
    requiresPermission: permissions.truck.addOrder
  },
  {
    title: "Set Timeframes",
    action: "order/setTimeframesShortcutAction",
    requiresPermission: permissions.order.setTimeFrame,
    requiresAgentPermission: permissions.agents.order.setTimeFrame,
  },
  {
    title: "View Notes",
    action: "order/notesShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.view.note,
    requiresAgentPermission: permissions.agents.order.view.note
  },
  {
    title: "Add Note",
    action: "order/addNoteShortcutAction",
    requiresPermission: permissions.order.add.note,
    requiresAgentPermission: permissions.agents.order.add.note,
  },
  {
    title: "Complete",
    action: "order/completeOrderShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.complete,
    requiresAgentPermission: permissions.agents.order.complete,
  },
  {
    title: "Cancel",
    action: "order/cancelOrderShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.cancel,
    requiresAgentPermission: permissions.agents.order.cancel,
  },
  {
    title: "Uncancel",
    action: "order/uncancelOrderShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.cancel,
    requiresAgentPermission: permissions.agents.order.cancel,
  },
  {
    title: "Message Customer",
    action: "order/messageCustomerShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.messageCustomer,
    requiresAgentPermission: permissions.agents.order.messageCustomer,
  },
  {
    title: "View Map",
    action: "order/orderMapShortcutAction",
    requiresPermission: permissions.order.view.consignee,
    requiresAgentPermission: permissions.agents.order.view.consignee,
  },
  {
    title: "Add Order Pallet",
    action: "order/addOrderPalletShortcutAction",
    requiresPermission: permissions.order.palletize,
    requiresAgentPermission: permissions.agents.order.palletize,
  }
];

const ORDER_SHORTCUT = [
  {
    title: "New Order",
    icon: "plus-circle",
    action: "order/newOrderShortcutAction",
    requiresPermission: permissions.order.create,
    requiresAgentPermission: permissions.agents.order.create,
  },
  { title: "Refresh", icon: "sync-alt", action: "order/reloadOrderShortcutAction" },
  {
    title: "Copy",
    icon: "copy",
    action: "order/copyOrderShortcutAction",
    requiresPermission: permissions.order.copy,
    requiresAgentPermission: permissions.agents.order.copy,
  },
  {
    title: "Save",
    icon: "save",
    action: "order/saveOrderShortcutAction",
    requiresPermission: permissions.order.save
  },
  {
    title: "Print BOL",
    icon: "print",
    action: "order/printBolShortcutAction",
    requiresPermission: permissions.order.printBol,
    requiresAgentPermission: permissions.agents.order.printBol,
  },
  {
    title: "Close",
    icon: "times-circle",
    action: "close",
    requiresPermission: permissions.order.close,
    requiresAgentPermission: permissions.agents.order.close,
  },
  {
    title: "Cancel",
    icon: "ban",
    action: "order/cancelOrderShortcutAction",
    requiresPermission: permissions.order.cancel,
    requiresAgentPermission: permissions.agents.order.cancel
  },
  {
    title: "Uncancel",
    icon: "trash-restore",
    action: "order/uncancelOrderShortcutAction",
    requiresPermission: permissions.order.cancel,
    requiresAgentPermission: permissions.agents.order.cancel
  },
  {
    title: "Receive",
    icon: "hands",
    action: "order/receiveOrderShortcutAction",
    requiresPermission: permissions.order.receive,
    requiresAgentPermission: permissions.agents.order.receive,
  },
  {
    title: "Customer Not Ready",
    icon: "user-check",
    action: "order/customerNotReadyShortcutAction",
    requiresPermission: permissions.order.customerNotReady,
    requiresAgentPermission: permissions.agents.order.customerNotReady
  },
  {
    title: "Schedule",
    icon: "calendar-alt",
    action: "order/scheduleOrderShortcutAction",
    requiresPermission: permissions.order.schedule,
    requiresAgentPermission: permissions.agents.order.schedule
  },
  {
    title: "Scheduled Delivery Date History",
    icon: "calendar-check",
    action: "order/deliveryDateHistoryLogsShortcutAction",
    requiresPermission: permissions.order.view.dates,
    requiresAgentPermission: permissions.agents.order.view.dates
  },

  {
    title: "Unschedule",
    icon: "calendar-times",
    action: "order/unscheduleOrderShortcutAction",
    requiresPermission: permissions.order.unschedule,
    requiresAgentPermission: permissions.agents.order.unschedule
  },
  {
    title: "Set Timeframes",
    icon: "clock",
    action: "order/setTimeframesShortcutAction",
    requiresPermission: permissions.order.setTimeFrame,
    requiresAgentPermission: permissions.agents.order.setTimeFrame,
  },
  {
    title: "Complete",
    icon: "check-square",
    action: "order/completeOrderShortcutAction",
    requiresPermission: permissions.order.complete,
    requiresAgentPermission: permissions.agents.order.complete
  },
  {
    title: "Notes",
    icon: "sticky-note",
    action: "order/notesShortcutAction",
    requiresPermission: permissions.order.view.note,
    requiresAgentPermission: permissions.agents.order.view.note
  },
  {
    title: "Calculate",
    icon: "calculator",
    action: "",
    requiresPermission: permissions.order.calculate,
    requiresAgentPermission: permissions.agents.order.calculate
  },
  {
    title: "Ships with Orders",
    icon: "layer-group",
    action: "order/shipsWithOrdersShortcutAction"
  },
  {
    title: "Add to Truck",
    icon: "truck",
    action: "order/addOrderToTruckShortcutAction",
    requiresPermission: permissions.truck.addOrder,
  },
  {
    title: "Remove from Truck",
    icon: "truck-loading",
    action: "order/removeOrderFromTruckShortcutAction",
    requiresPermission: permissions.truck.addOrder,
  },
  {
    title: "Message Customer",
    icon: "envelope",
    action: "order/messageCustomerShortcutAction",
    requiresPermission: permissions.order.messageCustomer,
    requiresAgentPermission: permissions.agents.order.messageCustomer
  },
  {
    title: "Audit Trail",
    icon: "list-alt",
    action: "order/orderAuditTrailShortcutAction",
    requiresPermission: permissions.order.view.audit,
    requiresAgentPermission: permissions.agents.order.view.audit,
  },
  {
    title: "View Map",
    icon: "map-marked-alt",
    action: "order/orderMapShortcutAction",
    requiresPermission: permissions.order.view.consignee,
    requiresAgentPermission: permissions.agents.order.view.consignee,
  },
  {
    title: "Add Order Pallet",
    icon: "pallet",
    action: "order/addOrderPalletShortcutAction",
    requiresPermission: permissions.order.palletize,
    requiresAgentPermission: permissions.agents.order.palletize,
  },
  {
    title: "View Barcode Order",
    icon: "barcode",
    action: "order/viewBarcodeOrderShortcutAction",
    requiresPermission: permissions.barcodeSystem.orders.view
  },
  {
    title: "Link Barcode Inventory",
    icon: "link",
    action: "barcodeOrder/linkInventoryToOrderShortcutAction",
    requiresPermission: permissions.barcodeSystem.inventory.link
  }
];

const TRUCK_SUMMARY_SHORTCUT = [
  {
    title: "Import Trucks",
    icon: "plus-circle",
    action: "truck/importTrucksShortcutAction",
    requiresPermission: permissions.truck.create
  },
  {
    title: "Unload Time Maintenance",
    icon: "sliders-h",
    action: "truck/commodityUnloadTimeOverridesShortcutAction",
    requiresPermission: permissions.manage.commodityUnloadTimeOverride
  },
  {
    title: "Driver Manifests",
    icon: "download",
    action: "truck/driverManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail
  }
];

const BARCODE_INVENTORY_SUMMARY_SHORTCUT = [
];

const BARCODE_INVENTORY_ITEMS_SHORTCUT = [
];
const BARCODE_BULK_INVENTORY_ITEMS_SHORTCUT = [
];
const BARCODE_PALLETS_SHORTCUT = [
];

const BARCODE_LOADS_SHORTCUT = [
];

const BARCODE_MANIFESTS_SHORTCUT = [
];

const BARCODE_ORDER_SEARCH_SHORTCUT = [
];

const STOCK_ORDER_SEARCH_SHORTCUT = [
];

const STOCK_ADJUSTMENT_SEARCH_SHORTCUT = [
];

const TRUCK_MENU_ITEM = [
  { title: "View Truck", action: "truck/viewTruckShortcutAction", maxSelection: 1 },
  {
    title: "Split Truck",
    action: "truck/splitTruckShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.split
  },
  {
    title: "Combine Trucks",
    action: "truck/combineTrucksShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.combine
  },
  {
    title: "Scheduled Timeframe Optimization",
    action: "truck/scheduleOptimizationShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.order.setTimeFrame
  },
  {
    title: "Print Manifest",
    action: "truck/printManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail,
    maxSelection: 1
  },
  {
    title: "Download Driver Manifest",
    action: "truck/downloadDriverManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail,
    maxSelection: 1
  },
  {
    title: "Export Manifest",
    action: "truck/exportManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail,
    maxSelection: 1
  },
  {
    title: "Delete Truck",
    action: "truck/deleteTruckShortcutAction",
    requiresPermission: permissions.truck.edit
  },
  {
    title: "View Truck Audit Log",
    action: "truck/viewTruckAuditShortcutAction",
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Finalize Truck",
    action: "truck/finalizeTruckShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.edit
  },
  {
    title: "Unfinalize Truck",
    action: "truck/unfinalizeTruckShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.edit
  },
  {
    title: "Map View",
    icon: "map",
    action: "truck/mapViewTruckShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Routing",
    action: "truck/routingTruckShortcutAction",
    maxSelection: 1,
    requiresPermission: permissions.truck.edit
  }
];
const TRUCK_SHORTCUT = [
  {
    title: "Map View",
    icon: "map",
    action: "truck/mapViewTruckShortcutAction",
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Split Truck",
    icon: "columns",
    action: "truck/splitTruckShortcutAction",
    requiresPermission: permissions.truck.split
  },
  {
    title: "Routing",
    icon: "sort-numeric-up",
    action: "truck/routingTruckShortcutAction",
    requiresPermission: permissions.truck.edit
  },
  {
    title: "Scheduled Timeframe Optimization",
    icon: "stopwatch",
    action: "truck/scheduleOptimizationShortcutAction",
    requiresPermission: permissions.order.setTimeFrame
  },
  {
    title: "Finalize Truck",
    icon: "phone",
    action: "truck/finalizeTruckShortcutAction",
    requiresPermission: permissions.truck.edit
  },
  {
    title: "Unfinalize Truck",
    icon: "phone-slash",
    action: "truck/unfinalizeTruckShortcutAction",
    requiresPermission: permissions.truck.edit
  },
  {
    title: "Print Manifest",
    icon: "print",
    action: "truck/printManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Download Driver Manifest",
    icon: "download",
    action: "truck/downloadDriverManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Export Manifest",
    icon: "file-export",
    action: "truck/exportManifestShortcutAction",
    requiresPermission: permissions.truck.view.detail
  },
  {
    title: "Unload Time Maintenance",
    icon: "sliders-h",
    action: "truck/commodityUnloadTimeOverridesShortcutAction",
    requiresPermission: permissions.manage.commodityUnloadTimeOverride
  }
];

const LOAD_MENU_ITEM = [
  { title: "View Load", action: "load/viewLoadShortcutAction", maxSelection: 1 },
  {
    title: "Unlock Load",
    action: "load/unlockLoadShortcutAction",
    requiresPermission: permissions.barcodeSystem.load.unlock
  },
  {
    title: "Consolidate Loads",
    action: "load/consolidateLoadsShortcutAction",
    requiresPermission: permissions.barcodeSystem.load.consolidate
  }
];

const MANIFEST_MENU_ITEM = [
  { title: "View Manifest", action: "manifest/viewManifestShortcutAction", maxSelection: 1 },
  {
    title: "Remove Manifest",
    action: "manifest/hideManifestShortcutAction",
    requiresPermission: permissions.barcodeSystem.manifest.hide
  },
  {
    title: "Restore Manifest",
    action: "manifest/restoreManifestShortcutAction",
    requiresPermission: permissions.barcodeSystem.manifest.restore
  }
];

const BARCODE_ORDER_MENU_ITEM = [
  { title: "View Barcode Order", action: "barcodeOrder/viewBarcodeOrderShortcutAction", maxSelection: 1 },
  {
    title: "Preview Order Information",
    action: "barcodeOrder/orderPreviewShortcutAction",
    maxSelection: 1
  },
  { title: "Open Order", action: "barcodeOrder/openOrderShortcutAction", maxSelection: 1 },
];

const STOCK_ORDER_MENU_ITEM = [
  { title: "View Order", action: "stockOrder/viewStockOrderShortcutAction", maxSelection: 1 },
  {
    title: "Cancel Order",
    action: "stockOrder/cancelStockOrderShortcutAction",
    requiresPermission: permissions.barcodeSystem.stockOrders.cancel
  },
  {
    title: "Unlock Order",
    action: "stockOrder/unlockStockOrderShortcutAction",
    requiresPermission: permissions.barcodeSystem.stockOrders.unlock
  }
];


const STOCK_ADJUSTMENT_MENU_ITEM = [
  { title: "View Adjustment", action: "stockAdjustment/viewStockAdjustmentShortcutAction", maxSelection: 1 },
  {
    title: "Delete Adjustment",
    action: "stockAdjustment/deleteStockOrderShortcutAction",
    requiresPermission: permissions.barcodeSystem.inventory.adjustStock,
    maxSelection: 1
  },
  {
    title: "Apply Adjustment",
    action: "stockOrder/applyStockAdjustmentShortcutAction",
    requiresPermission: permissions.barcodeSystem.inventory.adjustStock,
    maxSelection: 1
  }
];

const INVENTORY_ITEM_MENU_ITEM = [
  { title: "View Inventory Item", action: "inventory/viewInventoryItemShortcutAction", maxSelection: 1 },
  { title: "Resolve Missing Inventory Item", requiresPermission: permissions.barcodeSystem.inventory.resolve, action: "inventory/resolveMissingInventoryItemShortcutAction", maxSelection: 1 },
  { title: "View Model Link Summary", requiresPermission: permissions.barcodeSystem.inventory.view, action: "inventory/viewModelLinkSummaryShortcutAction", maxSelection: 1 }
];

const BULK_INVENTORY_ITEM_MENU_ITEM = [
  { title: "View Bulk Inventory Item", action: "inventory/viewBulkInventoryItemShortcutAction", maxSelection: 1 }
];

const PALLET_MENU_ITEM = [
  { title: "View Pallet", action: "inventory/viewPalletShortcutAction", maxSelection: 1 }
];


const API_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSSSSZZ";
const DISPLAY_DATE_FORMAT = "MM/DD/YYYY";
const DISPLAY_DATE_WITH_TIME_FORMAT = "MM/DD/YYYY hh:mm A";
const DISPATCH_ORDER_SETTINGS = ["filter", "download", "help", "fields"];
const SEARCH_ORDER_SETTINGS = ["download", "help", "fields"];
const ADVANCED_SEARCH_ORDER_SETTINGS = ["download", "help", "fields"];
const DISPATCH_TRUCK_SETTINGS = ["download", "new_truck", "fields"];
const TRUCKS_SETTINGS = ["help", "new_truck"];
const NOTE_VISIBLE = {
  0: "All",
  1: "CDS Only",
  2: "CDS & Customer Only",
  3: "CDS & Network Member Only"
};

const ORDER_FIELD = [
  { name: "selected", title: "", hidden: false },
  { name: "number", title: "SYS #", sortable: true, hidden: false },
  { name: "orderName", title: "CRN", sortable: true, hidden: false },
  { name: "truckName", title: "Truck Name", sortable: true, hidden: false },
  { name: "truckStatus", title: "Truck Status", sortable: true, hidden: false },
  { name: "consignee.name", title: "Name", sortable: true, hidden: false },
  { name: "consignee.address.streetLine1", title: "Address", sortable: true, hidden: false },
  { name: "consignee.address.streetLine2", title: "Address 2", sortable: true, hidden: false },
  { name: "consignee.address.city", title: "City", sortable: true, hidden: false },
  { name: "consignee.address.zipCode", title: "Zip", sortable: true, hidden: false },
  { name: "cabinetCount", title: "CabCnt", sortable: true, hidden: false },
  { name: "unitCount", title: "UnitCnt", sortable: true, hidden: false },
  { name: "cubes", title: "Cubes", sortable: true, hidden: false },
  { name: "shipDate", title: "Ship Date", sortable: true, formatter: formatDate, hidden: false },
  {
    name: "anticipatedReceiveDate",
    title: "Est. Recv Date",
    sortable: true,
    formatter: formatDate,
    hidden: false
  },
  {
    name: "actualReceiveDate",
    title: "Act. Recv Date",
    sortable: true,
    formatter: formatDate,
    hidden: false
  },
  { name: "RecvFlag", title: "Recv Flag", sortable: true, hidden: false },
  {
    name: "requestedDeliveryDate",
    title: "Req Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false
  },
  {
    name: "scheduledDeliveryDate",
    title: "Sched Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false
  },
  { name: "timeframe", title: "Timeframe", hidden: false },
  {
    name: "actualDeliveryDate",
    title: "Act Del Date",
    sortable: true,
    formatter: formatDate,
    hidden: false
  },
  { name: "facility", title: "Facility", sortable: true, hidden: false },
  { name: "remarks", title: "Remarks", sortable: true, hidden: false },
  { name: "specialRequests", title: "Special Req", sortable: true, hidden: false },
  { name: "manufacturer", title: "Manufacturer", sortable: true, hidden: false },
  { name: "deliveryType", title: "Del Type", sortable: true, hidden: false },
  { name: "BillTo", title: "Bill To", sortable: true, hidden: false },
  { name: "Carrier", title: "Carrier", sortable: true, hidden: false },
  { name: "Shipments", title: "Shipments", sortable: true, hidden: false },
  { name: "status", title: "Status", sortable: true, hidden: false },
  { name: "miles", title: "Miles", sortable: true, hidden: false },
  { name: "bookedBy", title: "Booked By", sortable: true, hidden: false },
  {
    name: "isAsapDelivery",
    title: "Wants Asap",
    formatter: formatBool,
    sortable: false,
    hidden: false
  },
  { name: "actions", title: "", hidden: false }
];

const DYNAMIC_DATE_OPTIONS = [
  { text: "No Date (12/31/2049)", value: "noDate"},
  { text: "Today", value: "today" },
  { text: "Yesterday", value: "yesterday" },
  { text: "Tomorrow", value: "tomorrow" },
  { text: "14 Days Ago", value: "14 days ago" },
  { text: "Last 7 Days", value: "last week" },
  { text: "Last 30 Days", value: "last month" },
  { text: "Last 90 Days", value: "last 3 months" },
  { text: "Last 180 Days", value: "last 6 months" },
  { text: "Next 7 Days", value: "next week" },
  { text: "Next 30 Days", value: "next month" }
];

const RED_MAP_PIN_ICON = {
  url: "https://mt.google.com/vt/icon?name=icons/spotlight/spotlight-waypoint-b.png",
  labelOrigin: { x: 11, y: 12 }
};
const GREEN_MAP_PIN_ICON = {
  url: "https://mt.google.com/vt/icon?name=icons/spotlight/spotlight-waypoint-a.png",
  labelOrigin: { x: 11, y: 12 }
};
const PURPLE_MAP_PIN_ICON = {
  url: "https://maps.gstatic.com/mapfiles/ms2/micons/purple-dot.png",
  labelOrigin: { x: 15, y: 9 }
}
const MAP_STYLES = {
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }]
    },
    {
      featureType: "transit",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }]
    }
  ]
};

const ORDER_TYPE_RETURN = "RET";
const REGEX_EMAIL_VALIDATE = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
const REGEX_PHONE_REPLACE = /(\d{0,3})(\d{0,3})(\d{0,4})/;
const REGEX_PHONE_VALIDATE = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;

export {
  ORDER_FILTER_FIELDS,
  ORDER_MENU_ITEM,
  ORDER_SHORTCUT,
  TRUCK_SHORTCUT,
  TRUCK_SUMMARY_SHORTCUT,
  BARCODE_INVENTORY_SUMMARY_SHORTCUT,
  BARCODE_INVENTORY_ITEMS_SHORTCUT,
  BARCODE_BULK_INVENTORY_ITEMS_SHORTCUT,
  BARCODE_PALLETS_SHORTCUT,
  BARCODE_LOADS_SHORTCUT,
  BARCODE_MANIFESTS_SHORTCUT,
  BARCODE_ORDER_SEARCH_SHORTCUT,
  STOCK_ORDER_SEARCH_SHORTCUT,
  STOCK_ADJUSTMENT_SEARCH_SHORTCUT,
  HOME_SHORTCUT,
  DISPATCH_SHORTCUT,
  TRUCK_MENU_ITEM,
  LOAD_MENU_ITEM,
  MANIFEST_MENU_ITEM,
  BARCODE_ORDER_MENU_ITEM,
  STOCK_ORDER_MENU_ITEM,
  STOCK_ADJUSTMENT_MENU_ITEM,
  INVENTORY_ITEM_MENU_ITEM,
  BULK_INVENTORY_ITEM_MENU_ITEM,
  PALLET_MENU_ITEM,
  API_DATE_FORMAT,
  DISPLAY_DATE_FORMAT,
  DISPATCH_ORDER_SETTINGS,
  SEARCH_ORDER_SETTINGS,
  DISPATCH_TRUCK_SETTINGS,
  TRUCKS_SETTINGS,
  NOTE_VISIBLE,
  ORDER_FIELD,
  DISPLAY_DATE_WITH_TIME_FORMAT,
  DYNAMIC_DATE_OPTIONS,
  ADVANCED_SEARCH_ORDER_SETTINGS,
  RED_MAP_PIN_ICON,
  GREEN_MAP_PIN_ICON,
  PURPLE_MAP_PIN_ICON,
  MAP_STYLES,
  ORDER_TYPE_RETURN,
  REGEX_EMAIL_VALIDATE,
  REGEX_PHONE_VALIDATE,
  REGEX_PHONE_REPLACE
};
